import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  VerifiedUserOutlined as VerifiedUserOutlinedIcon,
} from '@mui/icons-material';
import { BglPrimaryButton, BglPrimaryOutlinedButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { BrandGuidelinesAPI } from 'apis/brandGuidelinesAPI';
import type { BrandGuidelineEntry, BrandGuidelinePayload } from 'types/apis/brandGuidelinesInterfaces';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';

const emptyForm = (defaultCategory = ''): BrandGuidelinePayload => ({
  configKey: '',
  configType: '',
  category: defaultCategory,
});

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

interface BrandGuidelineFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues?: BrandGuidelineEntry;
  registeredCategories: string[];
  onClose: () => void;
  onSave: (payload: BrandGuidelinePayload) => Promise<void>;
}

const BrandGuidelineFormDialog: React.FC<BrandGuidelineFormDialogProps> = ({
  open,
  mode,
  initialValues,
  registeredCategories,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<BrandGuidelinePayload>(emptyForm());
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const categoryOptions = useMemo(() => {
    const merged = new Set<string>();

    registeredCategories.forEach((category) => merged.add(category));
    customCategories.forEach((category) => merged.add(category));
    if (form.category) merged.add(form.category);
    if (initialValues?.category) merged.add(initialValues.category);

    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }, [registeredCategories, customCategories, form.category, initialValues?.category]);

  useEffect(() => {
    if (!open) return;

    setCustomCategories([]);
    setIsAddingCategory(false);
    setNewCategoryName('');
    setCategoryError(null);

    if (mode === 'edit' && initialValues) {
      setForm({
        configKey: initialValues.configKey,
        configType: initialValues.configType,
        category: initialValues.category,
      });
    } else {
      setForm(emptyForm(registeredCategories[0] ?? ''));
    }

    setFormError(null);
  }, [open, mode, initialValues, registeredCategories]);

  const updateField = <K extends keyof BrandGuidelinePayload>(field: K, value: BrandGuidelinePayload[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      setCategoryError('Category name is required');
      return;
    }

    const exists = categoryOptions.some((category) => category.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setCategoryError('Category already exists');
      return;
    }

    setCustomCategories((prev) => [...prev, trimmed]);
    updateField('category', trimmed);
    setNewCategoryName('');
    setCategoryError(null);
    setIsAddingCategory(false);
  };

  const handleCancelAddCategory = () => {
    setIsAddingCategory(false);
    setNewCategoryName('');
    setCategoryError(null);
  };

  const validate = (): string | null => {
    if (!form.configKey.trim()) return 'Config key is required';
    if (!form.configType.trim()) return 'Config type is required';
    if (!form.category.trim()) return 'Category is required';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      await onSave({
        configKey: form.configKey.trim(),
        configType: form.configType.trim(),
        category: form.category.trim(),
      });
      onClose();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to save brand guideline');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Create Mapping' : 'Edit Mapping'}</DialogTitle>
      <DialogContent dividers>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Config Type"
            value={form.configType}
            onChange={(e) => updateField('configType', e.target.value)}
            required
            fullWidth
            disabled={mode === 'edit'}
            helperText={mode === 'edit' ? 'Config type cannot be changed' : 'e.g. email_template'}
          />

          <TextField
            label="Config Key"
            value={form.configKey}
            onChange={(e) => updateField('configKey', e.target.value)}
            required
            fullWidth
            disabled={mode === 'edit'}
            helperText={mode === 'edit' ? 'Config key cannot be changed' : 'e.g. logo_URL'}
          />

          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth required>
                <InputLabel id="brand-guideline-category-label">Category</InputLabel>
                <Select
                  labelId="brand-guideline-category-label"
                  label="Category"
                  value={form.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  displayEmpty
                >
                  {categoryOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No categories yet — add one with +
                    </MenuItem>
                  ) : (
                    categoryOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Tooltip title="Add new category">
                <IconButton
                  onClick={() => setIsAddingCategory(true)}
                  sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                  aria-label="Add new category"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {isAddingCategory && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1.5 }}>
                <TextField
                  label="New category"
                  value={newCategoryName}
                  onChange={(e) => {
                    setNewCategoryName(e.target.value);
                    setCategoryError(null);
                  }}
                  size="small"
                  fullWidth
                  autoFocus
                  error={Boolean(categoryError)}
                  helperText={categoryError ?? ' '}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCategory();
                    }
                    if (e.key === 'Escape') {
                      handleCancelAddCategory();
                    }
                  }}
                />
                <Tooltip title="Save category">
                  <IconButton color="primary" onClick={handleAddCategory} sx={{ mt: 0.5 }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton onClick={handleCancelAddCategory} sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <BglPrimaryOutlinedButton onClick={onClose} disabled={isSaving}>
          Cancel
        </BglPrimaryOutlinedButton>
        <PermissionGate require={UI_PAGES.BRAND_GUIDELINES.ctas.save.id} mode="disable">
          <BglPrimaryButton onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
          </BglPrimaryButton>
        </PermissionGate>
      </DialogActions>
    </Dialog>
  );
};

export const BrandGuidelinesAdminPage: React.FC = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const toast = useToast();

  const [entries, setEntries] = useState<BrandGuidelineEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedEntry, setSelectedEntry] = useState<BrandGuidelineEntry | undefined>();

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);

    const result = await BrandGuidelinesAPI.list(rc);

    if (!result.success) {
      setError(result.error ?? 'Failed to load brand guidelines');
      setEntries([]);
      setTotalCount(0);
    } else {
      setEntries(result.entries);
      setTotalCount(result.totalCount);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchEntries();
  }, [rc]);

  const registeredCategories = useMemo(
    () => Array.from(new Set(entries.map((entry) => entry.category).filter(Boolean))).sort(),
    [entries],
  );

  const filterCategories = useMemo(() => ['ALL', ...registeredCategories], [registeredCategories]);

  const filteredEntries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return entries.filter((entry) => {
      if (categoryFilter !== 'ALL' && entry.category !== categoryFilter) {
        return false;
      }

      if (!query) return true;

      return [entry.configKey, entry.configType, entry.category, `${entry.configType}.${entry.configKey}`]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [entries, searchQuery, categoryFilter]);

  const handleCreate = () => {
    setDialogMode('create');
    setSelectedEntry(undefined);
    setDialogOpen(true);
  };

  const handleEdit = async (entry: BrandGuidelineEntry) => {
    setDialogMode('edit');
    setSelectedEntry(entry);
    setDialogOpen(true);

    const result = await BrandGuidelinesAPI.getById(rc, entry.id);
    if (result.success && result.entry) {
      setSelectedEntry(result.entry);
    }
  };

  const handleSave = async (payload: BrandGuidelinePayload) => {
    const result =
      dialogMode === 'create'
        ? await BrandGuidelinesAPI.create(rc, payload)
        : await BrandGuidelinesAPI.update(rc, selectedEntry!.id, payload);

    if (!result.success) {
      throw new Error(result.error ?? 'Failed to save brand guideline');
    }

    toast.success(
      dialogMode === 'create'
        ? `Mapping ${payload.configType}.${payload.configKey} created`
        : `Mapping ${payload.configType}.${payload.configKey} updated`,
    );

    await fetchEntries();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4 }}>
        <Card sx={{ p: 0, borderRadius: 3, boxShadow: 3, bgcolor: theme.palette.background.paper, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 4, pt: 4, pb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VerifiedUserOutlinedIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
                <Typography variant="h4" fontWeight={600}>
                  Brand Guidelines
                </Typography>
              </Box>
              <Typography variant="subtitle1" color="text.secondary">
                Manage config-to-category mappings for brand guideline assets.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Tooltip title="Refresh list">
                <IconButton onClick={fetchEntries}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <PermissionGate require={UI_PAGES.BRAND_GUIDELINES.ctas.save.id} mode="disable">
                <BglPrimaryButton onClick={handleCreate} startIcon={<AddIcon />}>
                  New Mapping
                </BglPrimaryButton>
              </PermissionGate>
            </Box>
          </Box>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} action={<BglPrimaryOutlinedButton onClick={fetchEntries}>Retry</BglPrimaryOutlinedButton>}>
            {error}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 2, p: 3, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by config type, key, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 320, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="brand-guidelines-filter-category">Category</InputLabel>
              <Select
                labelId="brand-guidelines-filter-category"
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {filterCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Chip label={`${filteredEntries.length} of ${totalCount} mappings`} color="primary" variant="outlined" />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Config Type</TableCell>
                  <TableCell>Config Key</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        {entries.length === 0
                          ? 'No mappings found. Create your first entry to get started.'
                          : 'No entries match your search filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Typography component="code" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {entry.configType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="code" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {entry.configKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={entry.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(entry.createdAt)}</TableCell>
                      <TableCell>{formatDate(entry.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit mapping">
                          <IconButton onClick={() => handleEdit(entry)} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <BrandGuidelineFormDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={selectedEntry}
        registeredCategories={registeredCategories}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
};

export default BrandGuidelinesAdminPage;
